import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'

import { ArrowLeft, ArrowUpRight, Truck } from 'lucide-react'
import { pad } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ButtonLink } from '@/components/generals/button'
import { CtaBand } from '@/components/generals/cta-band'
import { SectionHeading } from '@/components/generals/section-heading'
import { TransitionLink } from '@/components/generals/transition-link'
import { Enter, HeroTitle } from '@/components/motion/hero-title'
import { Reveal } from '@/components/motion/reveal'
import { ServiceAreaMap } from '@/components/widgets/service-area-map'
import { ProjectCard } from '@/components/widgets/project-card'
import { AreaCard } from '@/components/widgets/area-card'
import { DISPATCH_NOTE, getAreaBySlug, serviceAreas } from '@/constants/areas'
import { projects } from '@/constants/projects'
import { services } from '@/constants/services'
import { siteConfig, QUOTE_PATH } from '@/constants/site'

interface AreaPageProps {
    slug: string
}

const AreaPage: FC<AreaPageProps> = ({ slug }): ReactNode => {
    const area = getAreaBySlug(slug)

    if (!area) return null

    const localProjects = projects.filter(project => project.areaSlug === area.slug)

    const nearby = serviceAreas
        .filter(item => item.slug !== area.slug)
        .sort((a, b) => Math.abs(a.arrivalMinutes - area.arrivalMinutes) - Math.abs(b.arrivalMinutes - area.arrivalMinutes))
        .slice(0, 3)

    const stats = [
        ...(area.doorsServiced ? [{ value: area.doorsServiced, label: "Doors worked on" }] : []),
        { value: area.arrival, label: "Usual urgent arrival" },
        { value: area.zips.join(', '), label: area.zips.length > 1 ? "ZIP codes" : "ZIP code" }
    ]

    return (
        <Layout>
            <Seo
                title={`Garage door repair in ${area.name}`}
                description={`${area.intro} We usually reach urgent calls here in ${area.arrival}. Springs, openers, off-track doors and new installs, priced in writing first.`}
            />

            <section className='relative overflow-hidden pb-16 pt-36 md:pb-24 md:pt-44'>
                <div aria-hidden='true' className='pointer-events-none absolute -right-40 -top-40 size-[42rem] rounded-full bg-ember/[0.07] blur-3xl' />

                <Container className='relative'>
                    <Enter>
                        <TransitionLink
                            href='/service-areas'
                            className='group mb-10 inline-flex items-center gap-2 text-sm font-medium text-slate transition-colors hover:text-ink'
                        >
                            <span className='flex size-8 items-center justify-center rounded-full border border-ink/12 transition-colors group-hover:border-ink/30'>
                                <ArrowLeft className='size-3.5 transition-transform duration-300 group-hover:-translate-x-0.5' aria-hidden='true' />
                            </span>
                            Back to service areas
                        </TransitionLink>
                    </Enter>

                    <div className='grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-10'>
                        <div className='lg:col-span-7'>
                            <Enter delay={60}>
                                <Eyebrow>Local service · {area.zips.join(', ')}</Eyebrow>
                            </Enter>

                            <HeroTitle
                                text={`Garage doors repaired and installed in ${area.name}`}
                                accent={area.name.split(' ')}
                                className='mt-6 max-w-[14ch] text-h1 font-semibold text-ink'
                            />

                            <Enter delay={420} className='mt-7 max-w-xl text-lead text-pretty text-slate'>
                                {area.intro}
                            </Enter>

                            <Enter delay={520} className='mt-10'>
                                <dl className='grid overflow-hidden rounded-[22px] border border-ink/10 bg-card' style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}>
                                    {stats.map((stat, index) => (
                                        <div key={stat.label} className={index > 0 ? 'border-l border-ink/10 p-4 sm:p-5' : 'p-4 sm:p-5'}>
                                            <dt className='font-mono text-[10px] uppercase tracking-[0.14em] text-slate sm:text-[11px]'>{stat.label}</dt>
                                            <dd className='mt-2 font-heading text-[1.05rem] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[1.4rem]'>{stat.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </Enter>

                            <Enter delay={620} className='mt-8 flex flex-wrap gap-3'>
                                <ButtonLink href={QUOTE_PATH} size='lg'>Request a price</ButtonLink>
                                <ButtonLink href={siteConfig.phoneHref} variant='outline' size='lg' icon='phone'>Call {siteConfig.phone}</ButtonLink>
                            </Enter>
                        </div>

                        <Enter delay={300} className='lg:col-span-5'>
                            <div className='relative overflow-hidden rounded-[32px] bg-ink p-5 text-cream sm:p-8'>
                                <div aria-hidden='true' className='door-grooves pointer-events-none absolute inset-0 opacity-30' />

                                <div className='relative'>
                                    <ServiceAreaMap activeSlug={area.slug} />

                                    <p className='mt-3 flex items-start gap-3 border-t border-cream/10 pt-5 text-[14px] leading-relaxed text-cream/70'>
                                        <Truck className='mt-0.5 size-4 shrink-0 text-ember-soft' aria-hidden='true' />
                                        {DISPATCH_NOTE}
                                    </p>
                                </div>
                            </div>
                        </Enter>
                    </div>
                </Container>
            </section>

            {area.quote && (
                <section id='customer' className='pb-20 md:pb-28'>
                    <Container>
                        <Reveal as='figure' className='mx-auto max-w-5xl border-y border-ink/10 py-14 text-center md:py-20'>
                            <span aria-hidden='true' className='block font-heading text-[5rem] leading-[0.6] text-ember'>“</span>

                            <blockquote className='mt-6 font-heading text-h2 font-semibold text-balance text-ink'>
                                {area.quote.text}
                            </blockquote>

                            <figcaption className='mt-8 font-mono text-[12px] uppercase tracking-[0.16em] text-slate'>
                                {area.quote.author}
                            </figcaption>
                        </Reveal>
                    </Container>
                </section>
            )}

            <section id='services' className='bg-paper py-24 md:py-32'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-4'>
                        <div className='lg:sticky lg:top-32'>
                            <SectionHeading
                                layout='stack'
                                eyebrow={`Available in ${area.name}`}
                                title="Our full list of services, here too."
                                titleClassName='max-w-[12ch]'
                                description="Whatever the job, it begins with a 21-point safety check and a price in writing."
                            />
                        </div>
                    </div>

                    <Reveal selector=':scope > li' as='ul' className='border-t border-ink/10 lg:col-span-8'>
                        {services.map((service, index) => (
                            <li key={service.slug}>
                                <TransitionLink
                                    href={service.href}
                                    className='group grid grid-cols-[2.5rem_1fr_auto] items-center gap-4 border-b border-ink/10 py-5 sm:grid-cols-[3rem_1fr_auto_auto] sm:gap-6'
                                >
                                    <span className='font-mono text-[12px] text-slate'>{pad(index + 1)}</span>

                                    <span className='min-w-0'>
                                        <span className='block font-heading text-[1.3rem] font-semibold leading-tight tracking-[-0.03em] text-ink transition-[color,transform,translate,scale,rotate] duration-500 ease-expo group-hover:translate-x-1.5 group-hover:text-ember sm:text-[1.55rem]'>
                                            {service.title}
                                        </span>

                                        <span className='mt-1 block font-mono text-[11px] uppercase tracking-[0.12em] text-slate sm:hidden'>
                                            {service.priceLabel}
                                        </span>
                                    </span>

                                    <span className='hidden text-right font-mono text-[12px] uppercase tracking-[0.12em] text-ink sm:block'>
                                        {service.priceLabel}
                                        <span className='block text-slate'>{service.meta}</span>
                                    </span>

                                    <span className='flex size-10 items-center justify-center rounded-full border border-ink/15 transition-[background-color,border-color,color,transform,translate,scale,rotate] duration-500 ease-expo group-hover:rotate-45 group-hover:border-ember group-hover:bg-ember group-hover:text-white'>
                                        <ArrowUpRight className='size-4' aria-hidden='true' />
                                    </span>
                                </TransitionLink>
                            </li>
                        ))}
                    </Reveal>
                </Container>
            </section>

            {localProjects.length > 0 && (
                <section id='local-work' className='py-24 md:py-32'>
                    <Container>
                        <SectionHeading
                            eyebrow={`Our work in ${area.name}`}
                            title="A job close to home."
                            action={<ButtonLink href='/projects' variant='outline'>See all projects</ButtonLink>}
                        />

                        <Reveal selector=':scope > *' className='mt-12 grid gap-8 md:grid-cols-2'>
                            {localProjects.map(project => (
                                <ProjectCard key={project.slug} project={project} />
                            ))}
                        </Reveal>
                    </Container>
                </section>
            )}

            <section id='nearby' className={localProjects.length > 0 ? 'pb-8' : 'py-24 md:py-32'}>
                <Container>
                    <SectionHeading
                        eyebrow="Close by"
                        title="Towns with similar arrival times."
                        action={<ButtonLink href='/service-areas' variant='outline'>See all eight towns</ButtonLink>}
                    />

                    <Reveal selector=':scope > li' as='ul' className='mt-12 grid gap-4 md:grid-cols-3'>
                        {nearby.map(item => (
                            <li key={item.slug}>
                                <AreaCard area={item} />
                            </li>
                        ))}
                    </Reveal>
                </Container>
            </section>

            <CtaBand
                eyebrow={area.name}
                title={`Live in ${area.name}? Get your price in writing.`}
                body="Tell us what the door is doing and we'll text a price range, usually within 30 minutes."
                primary={{ label: "Request a price", href: QUOTE_PATH }}
            />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: serviceAreas.map(area => ({ params: { slug: area.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<AreaPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!getAreaBySlug(slug)) return { notFound: true }

    return { props: { slug } }
}

export default AreaPage
